/**
 * Iter 86d — TOTP verification prompt.
 *
 * Shown when the user has TOTP enrolled but the current session hasn't
 * passed the 6-digit challenge yet AND there's no valid trusted-device
 * cookie. On success, the backend stamps the session as TOTP-passed
 * and refreshes the 7-day trusted-device cookie.
 */
import { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { ShieldCheck, Loader2 } from "lucide-react";

export default function TotpVerifyPrompt() {
  const { user, checkAuth, logout } = useAuth();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (!/^\d{6}$/.test(code)) {
      setError("Enter the 6-digit code from your authenticator app.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/totp-challenge", { code });
      await checkAuth();
    } catch (e) {
      setError(e?.response?.data?.detail || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="totp-verify-prompt">
      <Card className="w-full max-w-sm p-7 space-y-5 bg-white">
        <div className="space-y-1.5">
          <div className="flex items-center gap-2 text-emerald-700">
            <ShieldCheck className="h-5 w-5" />
            <div className="text-xs uppercase tracking-[0.18em] font-semibold">Two-factor sign-in</div>
          </div>
          <h1 className="font-display text-2xl tracking-tight text-slate-900">Enter your 6-digit code</h1>
          <p className="text-sm text-slate-600 leading-relaxed">
            Hi {user?.name?.split(" ")[0]} — open your authenticator app and enter the current code for <b>Close</b>.
          </p>
        </div>

        <div>
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            placeholder="123 456"
            className="text-center font-mono text-lg tracking-[0.4em] h-11"
            data-testid="totp-verify-input"
            autoFocus
          />
          {error && (
            <div className="text-xs text-red-600 mt-1.5" data-testid="totp-verify-error">{error}</div>
          )}
        </div>

        <Button
          onClick={submit}
          disabled={busy || code.length !== 6}
          className="w-full bg-slate-900 hover:bg-slate-800 text-white"
          data-testid="totp-verify-submit"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Verify
        </Button>

        <div className="text-[11px] text-slate-500 text-center border-t border-slate-100 pt-3 space-y-2">
          <div>This device will be remembered for 7 days after a successful code.</div>
          <button
            type="button"
            onClick={logout}
            className="underline hover:text-slate-700"
            data-testid="totp-verify-logout"
          >
            Sign in as a different user
          </button>
        </div>
      </Card>
    </div>
  );
}
